import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { mediateLoginInfoDependent } from 'mediators';
import { getLists, createList, deleteList } from 'core/services/lists.service';
import { getAccounts, Account } from 'core/services/accounts.service';
import { signOut } from 'core/services/auth.service';
import { EVENT_KEYS } from 'presentation/events';
import { Sidebar, SidebarBudget } from './Sidebar';
import {
    SelectListSignal,
} from 'presentation/events';

// ── Design tokens ─────────────────────────────────────────────────────────────

const C = {
    PURPLE: '#6344d4',
    PURPLE_STRONG: '#6d4ed5',
    PURPLE_SOFT: '#ede9fe',
    NAVY: '#1e3a8a',
    INK: '#0f172a',
    INK_700: '#334155',
    SLATE: '#64748b',
    SLATE_LIGHT: '#94a3b8',
    HAIR: '#e2e8f0',
    HAIR_SOFT: '#f1f5f9',
    BG: '#f8fafc',
    RED: '#dc2626',
    RED_BG: '#fef2f2',
    GREEN: '#16a34a',
    GREEN_BG: '#d1fae5',
    AMBER: '#f59e0b',
    AMBER_BG: '#fef3c7',
};

const LIST_COLORS = ['#1e3a8a', '#7c3aed', '#16a34a', '#dc2626', '#d97706', '#0891b2'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function hFmt(n: number | null | undefined, opts: { showSign?: boolean } = {}): string {
    const showSign = opts.showSign !== false;
    if (n == null) return '—';
    const num = Number(n);
    const abs = Math.abs(num).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!showSign) return '$' + abs;
    return (num < 0 ? '-$' : '$') + abs;
}

function generateSparkline(): number[] {
    const base = Math.random() * 3000 + 500;
    return Array.from({ length: 7 }, (_, i) => {
        const jitter = (Math.random() - 0.5) * 200;
        return Math.max(0, base + i * 50 + jitter);
    });
}

// ── Icon paths ────────────────────────────────────────────────────────────────

const ICON_PATHS: Record<string, string> = {
    plus:          'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
    warning:       'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
    trending_up:   'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    trending_down: 'M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z',
    archive:       'M20.54 5.23l-1.39-1.68A1.45 1.45 0 0018 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19a2 2 0 002 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l .94 1H5.12z',
    sort:          'M3 18h6v-2H3v2zM3 6v2h18V6H3zm0 7h12v-2H3v2z',
    more:          'M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    pause:         'M6 19h4V5H6v14zm8-14v14h4V5h-4z',
};

// ── Inline SVG Icon ───────────────────────────────────────────────────────────

interface IconProps {
    name: string;
    size?: number;
    color?: string;
}

function Icon({ name, size = 18, color = C.INK }: IconProps) {
    const path = ICON_PATHS[name];
    if (!path) {
        return <span style={{ width: size, height: size, display: 'inline-block', flexShrink: 0 }} />;
    }
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={color}
            style={{ display: 'inline-block', flexShrink: 0 }}
        >
            <path d={path} />
        </svg>
    );
}

// ── Sparkline ─────────────────────────────────────────────────────────────────

interface SparklineProps {
    data: number[];
    color?: string;
    width?: number;
    height?: number;
    strokeWidth?: number;
    fill?: boolean;
}

function Sparkline({ data, color = '#7c3aed', width = 64, height = 22, strokeWidth = 1.6, fill = true }: SparklineProps) {
    if (!data || data.length === 0) return null;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = data.length > 1 ? width / (data.length - 1) : 0;

    const pts = data.map((d, i) => {
        const x = i * stepX;
        const y = height - ((d - min) / range) * (height - 2) - 1;
        return [x, y];
    });

    const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
    const areaD = `${pathD} L ${width.toFixed(1)},${height} L 0,${height} Z`;
    const gradId = `spk-${Math.random().toString(36).slice(2, 7)}`;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            <defs>
                <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor={color} stopOpacity={0.25} />
                    <stop offset="1" stopColor={color} stopOpacity={0} />
                </linearGradient>
            </defs>
            {fill && <path d={areaD} fill={`url(#${gradId})`} />}
            <path d={pathD} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
    root: {
        width: '100%',
        height: '100vh',
        display: 'flex',
        background: C.BG,
        fontFamily: 'Inter, system-ui, sans-serif',
        color: C.INK,
        overflow: 'hidden',
    } as React.CSSProperties,

    main: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        padding: '32px 40px 64px',
        gap: 28,
        minWidth: 0,
    } as React.CSSProperties,

    head: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
    } as React.CSSProperties,

    h1: {
        fontSize: 32,
        fontWeight: 800,
        letterSpacing: '-0.025em',
        margin: 0,
        lineHeight: 1.1,
    } as React.CSSProperties,

    h1Sub: {
        fontSize: 14,
        color: C.SLATE,
        marginTop: 6,
    } as React.CSSProperties,

    headActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    } as React.CSSProperties,

    ghostBtn: {
        height: 40,
        padding: '0 14px',
        borderRadius: 10,
        background: '#fff',
        color: C.INK_700,
        border: `1px solid ${C.HAIR}`,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'inherit',
    } as React.CSSProperties,

    primaryBtn: {
        height: 40,
        padding: '0 18px',
        borderRadius: 10,
        background: C.INK,
        color: '#fff',
        border: 'none',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'inherit',
    } as React.CSSProperties,

    hero: {
        display: 'grid',
        gridTemplateColumns: '1.7fr 1fr 1fr',
        gap: 14,
    } as React.CSSProperties,

    heroCard: {
        background: '#fff',
        border: `1px solid ${C.HAIR}`,
        borderRadius: 14,
        padding: '20px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        position: 'relative',
        overflow: 'hidden',
    } as React.CSSProperties,

    heroDark: {
        background: C.INK,
        color: '#fff',
        border: 'none',
    } as React.CSSProperties,

    heroLab: {
        fontSize: 11,
        fontWeight: 700,
        color: C.SLATE_LIGHT,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    } as React.CSSProperties,

    heroLabDark: {
        color: 'rgba(255,255,255,0.55)',
    } as React.CSSProperties,

    heroVal: {
        fontSize: 36,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.025em',
        lineHeight: 1.05,
    } as React.CSSProperties,

    heroSub: {
        fontSize: 13,
        color: C.SLATE,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    } as React.CSSProperties,

    heroSubDark: {
        color: 'rgba(255,255,255,0.7)',
    } as React.CSSProperties,

    riskBox: {
        background: C.RED_BG,
        border: `1px solid ${C.RED}30`,
        borderRadius: 14,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
    } as React.CSSProperties,

    riskIco: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: '#fff',
        border: `1px solid ${C.RED}30`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    } as React.CSSProperties,

    riskMain: {
        flex: 1,
        minWidth: 0,
    } as React.CSSProperties,

    riskTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: C.RED,
    } as React.CSSProperties,

    riskSub: {
        fontSize: 13,
        color: C.INK_700,
        marginTop: 3,
    } as React.CSSProperties,

    riskBtn: {
        height: 36,
        padding: '0 14px',
        borderRadius: 8,
        background: '#fff',
        color: C.RED,
        border: `1px solid ${C.RED}30`,
        fontWeight: 700,
        fontSize: 13,
        cursor: 'pointer',
        fontFamily: 'inherit',
    } as React.CSSProperties,

    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    } as React.CSSProperties,

    sectionH: {
        fontSize: 18,
        fontWeight: 800,
        letterSpacing: '-0.01em',
        flex: 1,
    } as React.CSSProperties,

    chip: {
        height: 30,
        padding: '0 12px',
        borderRadius: 15,
        background: '#fff',
        border: `1px solid ${C.HAIR}`,
        fontSize: 12,
        fontWeight: 600,
        color: C.INK_700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'inherit',
    } as React.CSSProperties,

    chipActive: {
        background: C.INK,
        color: '#fff',
        borderColor: C.INK,
    } as React.CSSProperties,

    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 14,
    } as React.CSSProperties,

    card: {
        background: '#fff',
        border: `1px solid ${C.HAIR}`,
        borderRadius: 14,
        padding: '18px 20px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        position: 'relative',
        overflow: 'hidden',
    } as React.CSSProperties,

    cardAccent: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: 4,
    } as React.CSSProperties,

    cardHead: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
    } as React.CSSProperties,

    cardIcon: {
        width: 42,
        height: 42,
        borderRadius: 11,
        color: '#fff',
        fontSize: 16,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    } as React.CSSProperties,

    cardName: {
        fontSize: 15,
        fontWeight: 800,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties,

    cardMeta: {
        fontSize: 12,
        color: C.SLATE,
        marginTop: 3,
    } as React.CSSProperties,

    cardMore: {
        width: 30,
        height: 30,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: C.SLATE,
        marginLeft: 'auto',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        flexShrink: 0,
    } as React.CSSProperties,

    bigBal: {
        fontSize: 32,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.02em',
    } as React.CSSProperties,

    bigBalSub: {
        fontSize: 11,
        color: C.SLATE_LIGHT,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginTop: 2,
    } as React.CSSProperties,

    statsRow: {
        display: 'flex',
        gap: 0,
        borderTop: `1px solid ${C.HAIR_SOFT}`,
        paddingTop: 12,
        marginTop: 'auto',
    } as React.CSSProperties,

    statCell: {
        flex: 1,
        paddingRight: 12,
    } as React.CSSProperties,

    statLab: {
        fontSize: 10,
        fontWeight: 700,
        color: C.SLATE_LIGHT,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    } as React.CSSProperties,

    statVal: {
        fontSize: 14,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        marginTop: 3,
    } as React.CSSProperties,

    statSub: {
        fontSize: 11,
        color: C.SLATE,
        marginTop: 1,
    } as React.CSSProperties,

    chipRisk: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 10,
        background: C.RED_BG,
        color: C.RED,
        alignSelf: 'flex-start',
    } as React.CSSProperties,

    chipPaused: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 10,
        background: C.AMBER_BG,
        color: '#92400E',
        alignSelf: 'flex-start',
    } as React.CSSProperties,

    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 30,
    } as React.CSSProperties,

    modal: {
        width: '100%',
        maxWidth: 420,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
        padding: '28px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    } as React.CSSProperties,

    modalTitle: {
        fontSize: 18,
        fontWeight: 800,
    } as React.CSSProperties,

    fieldLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: C.INK_700,
        marginBottom: 6,
        display: 'block',
    } as React.CSSProperties,

    input: {
        width: '100%',
        height: 46,
        border: `1px solid ${C.HAIR}`,
        borderRadius: 10,
        padding: '0 14px',
        fontSize: 14,
        color: C.INK,
        fontFamily: 'inherit',
        outline: 'none',
        background: '#fff',
        boxSizing: 'border-box',
    } as React.CSSProperties,

    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
    } as React.CSSProperties,

    cancelBtn: {
        height: 40,
        padding: '0 16px',
        borderRadius: 8,
        background: 'none',
        color: C.SLATE,
        border: `1px solid ${C.HAIR}`,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
    } as React.CSSProperties,

    confirmBtn: {
        height: 40,
        padding: '0 18px',
        borderRadius: 8,
        background: C.INK,
        color: '#fff',
        border: 'none',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
    } as React.CSSProperties,

    confirmBtnDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    } as React.CSSProperties,
};

// ── Budget data shape ─────────────────────────────────────────────────────────

interface BudgetItem {
    id: string;
    displayName: string;
    balance: number;
    creditOwed: number;
    atRiskCount: number;
    paused: boolean;
    entries: number;
    lastUpdated: string;
    sparkline: number[];
    colorIdx: number;
    nextBillTitle: string | null;
    nextBillDate: string | null;
    nextBillAmount: number | null;
}

// ── BudgetCard ────────────────────────────────────────────────────────────────

interface BudgetCardProps {
    budget: BudgetItem;
    onSelect: (b: BudgetItem) => void;
}

function BudgetCard({ budget, onSelect }: BudgetCardProps) {
    const accentColor = LIST_COLORS[budget.colorIdx % LIST_COLORS.length];
    const budgetName = String(budget.displayName || '');
    const initial = budgetName.charAt(0).toUpperCase();
    const balance = Number(budget.balance);
    const creditOwed = Number(budget.creditOwed);

    const balColor = balance < 0 ? C.RED : balance < 500 ? '#9a3412' : C.INK;

    const balDisplay = hFmt(balance);
    const creditDisplay = creditOwed > 0 ? hFmt(creditOwed, { showSign: false }) : '—';
    const creditSub = creditOwed > 0 ? 'Across cards' : 'No balance';

    const nextBillAmountDisplay = budget.nextBillAmount != null ? hFmt(budget.nextBillAmount) : '—';
    const nextBillSub = budget.nextBillTitle
        ? `${String(budget.nextBillTitle)} · ${String(budget.nextBillDate || '')}`
        : 'None scheduled';

    return (
        <div
            style={S.card}
            onClick={() => onSelect(budget)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onSelect(budget); }}
            data-testid={`budget-card-${budget.id}`}
        >
            <div style={{ ...S.cardAccent, background: accentColor }} />

            <div style={S.cardHead}>
                <div style={{ ...S.cardIcon, background: accentColor }}>{initial}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.cardName}>{budgetName}</div>
                    <div style={S.cardMeta}>
                        {budget.entries} entries · Updated {String(budget.lastUpdated || '')}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        {budget.atRiskCount > 0 && (
                            <span style={S.chipRisk}>
                                <Icon name="warning" size={10} color={C.RED} />
                                {budget.atRiskCount} at risk
                            </span>
                        )}
                        {budget.paused && (
                            <span style={S.chipPaused}>
                                <Icon name="pause" size={10} color="#92400E" />
                                Paused
                            </span>
                        )}
                    </div>
                </div>
                <button style={S.cardMore} aria-label="More options" onClick={(e) => e.stopPropagation()}>
                    <Icon name="more" size={16} color={C.SLATE_LIGHT} />
                </button>
            </div>

            <div>
                <div style={S.bigBalSub}>Balance</div>
                <div style={{ ...S.bigBal, color: balColor }}>{balDisplay}</div>
            </div>

            <div style={{ margin: '0 -4px' }}>
                <Sparkline data={budget.sparkline} color={accentColor} width={280} height={42} strokeWidth={1.8} />
            </div>

            <div style={S.statsRow}>
                <div style={S.statCell}>
                    <div style={S.statLab}>Credit owed</div>
                    <div style={S.statVal}>{creditDisplay}</div>
                    <div style={S.statSub}>{creditSub}</div>
                </div>
                <div style={S.statCell}>
                    <div style={S.statLab}>Next bill</div>
                    {budget.nextBillTitle ? (
                        <>
                            <div style={{ ...S.statVal, color: C.RED }}>{nextBillAmountDisplay}</div>
                            <div style={S.statSub}>{nextBillSub}</div>
                        </>
                    ) : (
                        <>
                            <div style={{ ...S.statVal, color: C.SLATE_LIGHT }}>—</div>
                            <div style={S.statSub}>None scheduled</div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Filter chips ──────────────────────────────────────────────────────────────

type FilterKey = 'all' | 'atRisk' | 'recent' | 'balance';

// ── HomePage ──────────────────────────────────────────────────────────────────

export const HomePage = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const login: any = useSelector(mediateLoginInfoDependent);

    const [selectListSignal] = useState(new SelectListSignal());

    const [budgets, setBudgets] = useState<BudgetItem[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);

    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState<FilterKey>('all');

    // New budget dialog
    const [openNew, setOpenNew] = useState(false);
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);

    // Recover dialog
    const [openRecover, setOpenRecover] = useState(false);
    const [recoverName, setRecoverName] = useState('');
    const [recoverId, setRecoverId] = useState('');

    const userId = String(login?.user?.id || '');
    const userEmail = String(login?.user?.email || '');

    const loadData = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const [listsResult, accsResult] = await Promise.all([
                getLists(userId),
                getAccounts(userId),
            ]);

            const rawLists = listsResult?.value ?? [];
            const mappedBudgets: BudgetItem[] = rawLists.map((l: any, idx: number) => ({
                id: String(l.id || ''),
                displayName: String(l.displayName || ''),
                balance: Number(l.startingBalance) || 0,
                creditOwed: Number(l.creditOwed) || 0,
                atRiskCount: 0,
                paused: false,
                entries: 0,
                lastUpdated: 'recently',
                sparkline: generateSparkline(),
                colorIdx: idx,
                nextBillTitle: null,
                nextBillDate: null,
                nextBillAmount: null,
            }));

            setBudgets(mappedBudgets);
            setAccounts(Array.isArray(accsResult) ? accsResult : []);
        } catch {
            // silently fail — data stays empty
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (login === null || login === undefined) {
            // wait for auth check before redirecting
        }
    }, [login]);

    const handleLogout = () => {
        signOut();
        dispatch({ type: EVENT_KEYS.LOGIN, payload: null });
        window.location.href = '/';
    };

    const handleSelectBudget = (budget: SidebarBudget) => {
        // Find the full budget object and dispatch select signal
        const found = budgets.find((b) => b.id === budget.id);
        if (found) {
            selectListSignal.dispatch({ id: found.id, displayName: found.displayName });
        }
        history.push('/projection');
    };

    const handleBudgetCardSelect = (budget: BudgetItem) => {
        selectListSignal.dispatch({ id: budget.id, displayName: budget.displayName });
        history.push('/projection');
    };

    const handleCreateBudget = async () => {
        if (!newName.trim() || !userId) return;
        setSaving(true);
        try {
            await createList(userId, { displayName: newName.trim() });
            setOpenNew(false);
            setNewName('');
            await loadData();
            history.push('/projection');
        } catch {
            // silently fail
        } finally {
            setSaving(false);
        }
    };

    const handleRecover = async () => {
        if (!recoverName.trim() || !userId) return;
        setSaving(true);
        try {
            const payload: { displayName: string; id?: string } = { displayName: recoverName.trim() };
            if (recoverId.trim()) {
                payload.id = recoverId.trim();
            }
            await createList(userId, payload);
            setOpenRecover(false);
            setRecoverName('');
            setRecoverId('');
            await loadData();
            history.push('/projection');
        } catch {
            // silently fail
        } finally {
            setSaving(false);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleDeleteBudget = async (budget: BudgetItem, evt: React.MouseEvent) => {
        evt.stopPropagation();
        const budgetName = String(budget.displayName || budget.id);
        const confirmed = window.confirm(`Delete "${budgetName}"? This cannot be undone.`);
        if (!confirmed || !userId) return;
        try {
            await deleteList(userId, budget.id);
            await loadData();
        } catch {
            // silently fail
        }
    };

    // Compute hero metrics
    const cashAccounts = accounts.filter((a) => a.type === 'cash');
    const creditAccounts = accounts.filter((a) => a.type === 'credit');

    const totalCash = cashAccounts.reduce((sum, a) => sum + Number(a.balance), 0);
    const creditOwedTotal = creditAccounts.reduce((sum, a) => sum + Number(a.balance), 0);
    const netWorth = totalCash - creditOwedTotal;

    const netWorthColor = netWorth >= 0 ? C.GREEN : C.RED;

    const totalCashDisplay = hFmt(totalCash);
    const creditOwedDisplay = hFmt(creditOwedTotal, { showSign: false });
    const netWorthDisplay = hFmt(netWorth);

    // Budget lists
    const activeBudgets = budgets.filter((b) => !b.paused);
    const pausedBudgets = budgets.filter((b) => b.paused);

    const totalAtRisk = activeBudgets.reduce((sum, b) => sum + b.atRiskCount, 0);
    const atRiskBudgetCount = activeBudgets.filter((b) => b.atRiskCount > 0).length;

    // Hero sparkline — sum across all active budgets
    const heroSpark = activeBudgets.length > 0
        ? activeBudgets[0].sparkline.map((_, i) =>
            activeBudgets.reduce((sum, b) => sum + (b.sparkline[i] || 0), 0)
          )
        : generateSparkline();

    // Apply filter
    const applyFilter = (list: BudgetItem[]): BudgetItem[] => {
        if (activeFilter === 'atRisk') return list.filter((b) => b.atRiskCount > 0);
        if (activeFilter === 'recent') return [...list].sort((a, b) => a.lastUpdated.localeCompare(b.lastUpdated));
        if (activeFilter === 'balance') return [...list].sort((a, b) => b.balance - a.balance);
        return list;
    };

    const filteredActive = applyFilter(activeBudgets);

    const subtitleText = `${activeBudgets.length} active · ${pausedBudgets.length} paused`;

    const sidebarBudgets: SidebarBudget[] = budgets.map((b) => ({ id: b.id, displayName: b.displayName }));

    if (!login) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.BG }}>
                <div style={{ color: C.SLATE, fontSize: 14 }}>Loading…</div>
            </div>
        );
    }

    return (
        <div style={S.root}>
            {/* Sidebar */}
            <Sidebar
                budgets={sidebarBudgets}
                userId={userId}
                userEmail={userEmail}
                onSelectBudget={handleSelectBudget}
                onLogout={handleLogout}
            />

            {/* Main content */}
            <div style={S.main}>
                {/* Header */}
                <div style={S.head}>
                    <div>
                        <h1 style={S.h1}>Your Budgets</h1>
                        <div style={S.h1Sub}>{subtitleText}</div>
                    </div>
                    <div style={S.headActions}>
                        <button
                            style={S.ghostBtn}
                            onClick={() => setOpenRecover(true)}
                            data-testid="recover-btn"
                        >
                            <Icon name="archive" size={14} color={C.INK_700} />
                            Recover
                        </button>
                        <button
                            style={S.primaryBtn}
                            onClick={() => setOpenNew(true)}
                            data-testid="new-budget-btn"
                        >
                            <Icon name="plus" size={14} color="#fff" />
                            New budget
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 60, color: C.SLATE }}>
                        Loading…
                    </div>
                ) : (
                    <>
                        {/* Hero cards */}
                        <div style={S.hero} data-testid="hero-section">
                            {/* Total cash — dark card */}
                            <div style={{ ...S.heroCard, ...S.heroDark }}>
                                <div style={{ ...S.heroLab, ...S.heroLabDark }}>Total cash · all budgets</div>
                                <div style={S.heroVal} data-testid="total-cash-value">{totalCashDisplay}</div>
                                <div style={{ ...S.heroSub, ...S.heroSubDark }}>
                                    <Icon name="trending_up" size={14} color="#34d399" />
                                    Available across accounts
                                </div>
                                <div style={{ marginTop: 8, opacity: 0.85 }}>
                                    <Sparkline data={heroSpark} color="#a78bfa" width={200} height={48} strokeWidth={2} fill={true} />
                                </div>
                            </div>

                            {/* Credit owed */}
                            <div style={S.heroCard} data-testid="credit-owed-card">
                                <div style={S.heroLab}>Credit owed</div>
                                <div style={S.heroVal}>{creditOwedDisplay}</div>
                                <div style={S.heroSub}>
                                    <Icon name="trending_down" size={14} color={C.GREEN} />
                                    Balance on credit cards
                                </div>
                            </div>

                            {/* Net worth */}
                            <div style={S.heroCard} data-testid="net-worth-card">
                                <div style={S.heroLab}>Net worth</div>
                                <div style={{ ...S.heroVal, color: netWorthColor }}>{netWorthDisplay}</div>
                                <div style={S.heroSub}>Balance minus owed</div>
                            </div>
                        </div>

                        {/* At-risk callout */}
                        {totalAtRisk > 0 && (
                            <div style={S.riskBox} data-testid="risk-callout">
                                <div style={S.riskIco}>
                                    <Icon name="warning" size={20} color={C.RED} />
                                </div>
                                <div style={S.riskMain}>
                                    <div style={S.riskTitle}>
                                        {totalAtRisk} {totalAtRisk === 1 ? 'entry' : 'entries'} at risk across {atRiskBudgetCount} {atRiskBudgetCount === 1 ? 'budget' : 'budgets'}
                                    </div>
                                    <div style={S.riskSub}>
                                        These bills will push your balance negative before next payday.
                                    </div>
                                </div>
                                <button
                                    style={S.riskBtn}
                                    onClick={() => history.push('/entries')}
                                >
                                    Review →
                                </button>
                            </div>
                        )}

                        {/* Filter toolbar */}
                        <div style={S.toolbar}>
                            <div style={S.sectionH}>Active budgets</div>
                            <button
                                style={activeFilter === 'all' ? { ...S.chip, ...S.chipActive } : S.chip}
                                onClick={() => setActiveFilter('all')}
                            >
                                All active
                            </button>
                            <button
                                style={activeFilter === 'atRisk' ? { ...S.chip, ...S.chipActive } : S.chip}
                                onClick={() => setActiveFilter('atRisk')}
                            >
                                At risk
                            </button>
                            <button
                                style={activeFilter === 'recent' ? { ...S.chip, ...S.chipActive } : S.chip}
                                onClick={() => setActiveFilter('recent')}
                            >
                                Recent
                            </button>
                            <button
                                style={activeFilter === 'balance' ? { ...S.chip, ...S.chipActive } : S.chip}
                                onClick={() => setActiveFilter('balance')}
                            >
                                <Icon name="sort" size={12} color={activeFilter === 'balance' ? '#fff' : C.INK_700} />
                                Balance
                            </button>
                        </div>

                        {/* Active budget grid */}
                        {filteredActive.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: C.SLATE_LIGHT }}>
                                <div style={{ fontSize: 16, fontWeight: 600, color: C.SLATE, marginBottom: 8 }}>No budgets yet</div>
                                <div style={{ fontSize: 14 }}>Create your first budget to start tracking.</div>
                            </div>
                        ) : (
                            <div style={S.grid}>
                                {filteredActive.map((budget) => (
                                    <BudgetCard
                                        key={budget.id}
                                        budget={budget}
                                        onSelect={handleBudgetCardSelect}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Paused section */}
                        {pausedBudgets.length > 0 && (
                            <>
                                <div style={S.toolbar}>
                                    <div style={S.sectionH}>Paused</div>
                                </div>
                                <div style={S.grid}>
                                    {pausedBudgets.map((budget) => (
                                        <BudgetCard
                                            key={budget.id}
                                            budget={budget}
                                            onSelect={handleBudgetCardSelect}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* New budget dialog */}
            {openNew && (
                <div style={S.overlay} onClick={() => setOpenNew(false)}>
                    <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={S.modalTitle}>New budget</div>
                        <div>
                            <label style={S.fieldLabel} htmlFor="new-budget-name">Budget name</label>
                            <input
                                id="new-budget-name"
                                style={S.input}
                                placeholder="e.g. May 2026"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateBudget(); }}
                                autoFocus
                                data-testid="new-budget-name-input"
                            />
                        </div>
                        <div style={S.modalActions}>
                            <button style={S.cancelBtn} onClick={() => { setOpenNew(false); setNewName(''); }}>
                                Cancel
                            </button>
                            <button
                                style={newName.trim() && !saving ? S.confirmBtn : { ...S.confirmBtn, ...S.confirmBtnDisabled }}
                                onClick={handleCreateBudget}
                                disabled={!newName.trim() || saving}
                                data-testid="create-budget-submit"
                            >
                                {saving ? 'Creating…' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recover budget dialog */}
            {openRecover && (
                <div style={S.overlay} onClick={() => setOpenRecover(false)}>
                    <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={S.modalTitle}>Recover budget</div>
                        <div style={{ fontSize: 13, color: C.SLATE }}>
                            Re-connect to an existing budget by entering its name and list ID.
                        </div>
                        <div>
                            <label style={S.fieldLabel} htmlFor="recover-name">Budget name</label>
                            <input
                                id="recover-name"
                                style={S.input}
                                placeholder="e.g. March 2026"
                                value={recoverName}
                                onChange={(e) => setRecoverName(e.target.value)}
                                autoFocus
                                data-testid="recover-name-input"
                            />
                        </div>
                        <div>
                            <label style={S.fieldLabel} htmlFor="recover-id">List ID</label>
                            <input
                                id="recover-id"
                                style={S.input}
                                placeholder="xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
                                value={recoverId}
                                onChange={(e) => setRecoverId(e.target.value)}
                                data-testid="recover-id-input"
                            />
                        </div>
                        <div style={S.modalActions}>
                            <button
                                style={S.cancelBtn}
                                onClick={() => { setOpenRecover(false); setRecoverName(''); setRecoverId(''); }}
                            >
                                Cancel
                            </button>
                            <button
                                style={recoverName.trim() && recoverId.trim() && !saving ? S.confirmBtn : { ...S.confirmBtn, ...S.confirmBtnDisabled }}
                                onClick={handleRecover}
                                disabled={!recoverName.trim() || !recoverId.trim() || saving}
                            >
                                {saving ? 'Recovering…' : 'Recover'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
